import { numberToCurrency } from "helpers/string"

/**
 * Computes the guest-visible portion of the platform tax based on platform_fee_setting.
 * guide_pays  → guest pays $0
 * guest_pays  → guest pays full amount
 * split+%     → guest pays (100 - guide_portion)% of the tax
 * split+fixed → guest pays max(0, tax - guide_portion)
 */
export const applyPlatformFeeSetting = (rawTaxAmount, platformFeeSetting) => {
  if (!platformFeeSetting) return rawTaxAmount
  const { fee_payer_mode, split_type, guide_portion } = platformFeeSetting
  if (fee_payer_mode === "guide_pays") return 0
  if (fee_payer_mode === "guest_pays") return rawTaxAmount
  if (fee_payer_mode === "split") {
    const guideShare = Number(guide_portion) || 0
    if (split_type === "fixed") return Math.max(0, rawTaxAmount - guideShare)
    // percentage
    return rawTaxAmount * (1 - guideShare / 100)
  }
  return rawTaxAmount
}

/**
 * Calculates the total booking amount and breakdown
 * @param {Object} bookingSelections - The booking selections object
 * @param {Object} counts - Guest counts
 * @param {Number} guideFeePercent - Guide fee percentage
 * @param {Object} options - Additional options
 * @param {String} options.paymentType - Payment type (cash, card, link, etc)
 * @param {Boolean} options.isEdit - Whether this is an edit operation
 * @param {Number} options.originalTaxAndFee - Original tax and fee amount from booking.payment_summary.tax_and_fee
 * @param {Array} options.customFees - Custom fees from assigned_custom_fees (e.g. [{ id, title, fee_type: "fixed"|"percentage", price }])
 * @param {Object} options.platformFeeSetting - Platform fee setting ({ fee_payer_mode, split_type?, guide_portion?, card_fee_passthrough_enabled? })
 * @param {Object} options.cardFeeInfo - Card fee info from selected payment method ({ rate, fixed, formula })
 */
export const calculateBookingTotal = (bookingSelections, counts, guideFeePercent, options = {}) => {
  const { paymentType, isEdit, originalTaxAndFee, customFees = [], platformFeeSetting, cardFeeInfo } = options
  let total = 0
  const breakdown = []
  const { pricing_type, flat_rate, price_per_person, price_per_child, price_per_infant, price_per_passenger, included_guests, max_guests } =
    bookingSelections?.tripLength?.price || {}

  const getPrice = (specificPrice) => Number.parseFloat(specificPrice ?? 0) || 0

  const mainCounts = counts?.main || {}
  const extraCounts = counts?.extra || {}

  // Merge counts for calculation if pricing is flat rate, assuming all inputs flow into main or separate inputs need merging
  // If we unify inputs in GuestInputGroup, mainCounts will hold everything.
  // But we handle both for safety.
  const totalCounts = {
    adults: (mainCounts.adults || 0) + (extraCounts.adults || 0),
    children: (mainCounts.children || 0) + (extraCounts.children || 0),
    infants: (mainCounts.infants || 0) + (extraCounts.infants || 0),
    passengers: (mainCounts.passengers || 0) + (extraCounts.passengers || 0)
  }

  if (pricing_type === "charge_flat_rate") {
    const flatAmount = getPrice(flat_rate)
    const hasGuests = Object.values(totalCounts).some((val) => val > 0)

    if (hasGuests) {
      total += flatAmount

      const guestsIncluded = included_guests ?? max_guests ?? 0
      let remainingCapacity = guestsIncluded

      // 1. Adults
      const adultsIncluded = Math.min(totalCounts.adults, remainingCapacity)
      remainingCapacity = Math.max(0, remainingCapacity - adultsIncluded)

      // 2. Children
      const childrenIncluded = Math.min(totalCounts.children, remainingCapacity)
      remainingCapacity = Math.max(0, remainingCapacity - childrenIncluded)

      // 3. Infants
      const infantsIncluded = Math.min(totalCounts.infants, remainingCapacity)

      const extraAdults = Math.max(0, totalCounts.adults - adultsIncluded)
      const extraChildren = Math.max(0, totalCounts.children - childrenIncluded)
      const extraInfants = Math.max(0, totalCounts.infants - infantsIncluded)

      breakdown.push({
        label: "Flat Rate",
        amount: numberToCurrency(flatAmount),
        pricingDetails: {
          pricing_type: "charge_flat_rate",
          included_guests: guestsIncluded,
          price_per_person,
          price_per_child,
          price_per_infant
        }
      })

      if (extraAdults > 0) {
        const lineTotal = extraAdults * getPrice(price_per_person)
        total += lineTotal
        breakdown.push({ label: `Extra Adult x ${extraAdults}`, amount: numberToCurrency(lineTotal) })
      }
      if (extraChildren > 0) {
        const lineTotal = extraChildren * getPrice(price_per_child)
        total += lineTotal
        breakdown.push({ label: `Extra Child x ${extraChildren}`, amount: numberToCurrency(lineTotal) })
      }
      if (extraInfants > 0) {
        const lineTotal = extraInfants * getPrice(price_per_infant)
        total += lineTotal
        breakdown.push({ label: `Extra Infant x ${extraInfants}`, amount: numberToCurrency(lineTotal) })
      }
      if (totalCounts.passengers > 0) {
        const lineTotal = totalCounts.passengers * getPrice(price_per_passenger)
        total += lineTotal
        breakdown.push({ label: `Extra Passenger x ${totalCounts.passengers}`, amount: numberToCurrency(lineTotal) })
      }
    }
  } else {
    // Per Person Pricing
    const guestMapping = [
      { key: "adults", label: "Adult", price: price_per_person },
      { key: "children", label: "Child", price: price_per_child },
      { key: "infants", label: "Infant", price: price_per_infant },
      { key: "passengers", label: "Passenger", price: price_per_passenger }
    ]

    for (const { key, label, price } of guestMapping) {
      const count = totalCounts[key] || 0
      if (count > 0) {
        const unitPrice = getPrice(price)
        const lineTotal = unitPrice * count
        total += lineTotal
        breakdown.push({
          label: `${label} x ${count}`,
          amount: numberToCurrency(lineTotal)
        })
      }
    }
  }

  // Save guest subtotal before adding fees (used as base for percentage-based fees)
  const guestSubtotal = total

  // Tax & fee base includes selected add-ons so percentage-based fees apply on the full
  // taxable amount (guest charges + add-ons), not just the guest charges.
  const addOnsTotal = Number(bookingSelections?.addOnsTotal) || 0
  const taxableBase = guestSubtotal + addOnsTotal

  // Custom fees (fixed or percentage of taxable base)
  const customFeeItems = []
  let customFeeTotal = 0
  if (taxableBase > 0 && customFees?.length > 0) {
    for (const fee of customFees) {
      const feeAmount =
        fee.fee_type === "percentage"
          ? (taxableBase * Number(fee.price)) / 100
          : Number(fee.price)
      total += feeAmount
      customFeeTotal += feeAmount
      customFeeItems.push({ label: fee.title, amount: numberToCurrency(feeAmount) })
    }
  }

  // Determine the fee percentage to use based on payment type and mode
  let effectiveFeePercent = guideFeePercent
  let isCashPayment = false
  let fixedTaxAmount = null

  if (paymentType === "cash") {
    isCashPayment = true
    if (isEdit && originalTaxAndFee !== undefined && originalTaxAndFee !== null) {
      // For edit mode with cash payment, use the original fixed tax amount
      fixedTaxAmount = Number(originalTaxAndFee)
      effectiveFeePercent = 0 // Don't calculate percentage-based fee
    } else {
      // For add mode with cash payment, use 0% tax & fees
      effectiveFeePercent = 0
    }
  }

  const shouldApplyCardFee = paymentType === "card" && platformFeeSetting?.card_fee_passthrough_enabled && cardFeeInfo

  // Show Tax & Fees line if we have a total and either:
  // - There's a fee percentage, OR
  // - It's cash payment (to show fixed amount or $0.00), OR
  // - There are custom fees to display, OR
  // - Card processing fee passthrough is enabled
  if (total > 0 && (effectiveFeePercent || isCashPayment || customFeeItems.length > 0 || shouldApplyCardFee)) {
    let platformTaxAmount = 0
    if (effectiveFeePercent || isCashPayment) {
      if (fixedTaxAmount === null) {
        // Calculate based on percentage of the taxable base (guests + add-ons), then apply platform fee split
        const rawTaxAmount = (taxableBase * (effectiveFeePercent || 0)) / 100
        platformTaxAmount = applyPlatformFeeSetting(rawTaxAmount, platformFeeSetting)
      } else {
        // Cash edit: originalTaxAndFee is the already-split guest amount — use as-is
        platformTaxAmount = fixedTaxAmount
      }
      total += platformTaxAmount
    }

    const allChildren = [...customFeeItems]
    if (platformTaxAmount > 0) {
      allChildren.push({ label: "Platform Fee", amount: numberToCurrency(platformTaxAmount) })
    }

    let cardProcessingFee = 0
    if (shouldApplyCardFee) {
      cardProcessingFee = total * cardFeeInfo.rate + cardFeeInfo.fixed
      total += cardProcessingFee
      allChildren.push({ label: "Card Processing Fee", amount: numberToCurrency(cardProcessingFee) })
    }

    breakdown.push({
      label: "Tax & Fees",
      amount: numberToCurrency(customFeeTotal + platformTaxAmount + cardProcessingFee),
      children: allChildren.length > 0 ? allChildren : undefined
    })
  }

  return { total, guestSubtotal, breakdown }
}
